import { Currency as LegacyCurrency } from '@app/core/resources/currency';
import { Network as LegacyNetwork } from '@app/core/resources/networks';
import { Rate as LegacyRate } from '@app/core/resources/rate';
import { System as LegacySystem } from '@app/core/resources/system';
import { CountryEnum } from 'common/domain/country/entities/country.enum';
import { Currency } from 'common/systems/domain/resources/currency.resource';
import { Network } from 'common/systems/domain/resources/network.resource';
import { Rate } from 'common/systems/domain/resources/rate.resource';
import { System } from 'common/systems/domain/resources/system.resource';

/**
 * Single mapping function between the legacy `System`
 * (`@app/core/resources/system.ts`) and the common `System`
 * (`common/systems/domain/resources/system.resource.ts`). Lives in
 * infrastructure because it's the only layer of the `systems` capability
 * allowed to know the legacy type (design.md Decision 4 of
 * `systems-capability`). Both `SystemsRepositoryAdapter` and
 * `SystemsRealtimeAdapter` reuse this same function so the list of mapped
 * fields never diverges between the two adapters.
 */
export function mapLegacySystemToCommon(legacySystem: LegacySystem): System {
    const system: System = new System();

    system.id = legacySystem.id;
    system.attributes = {
        ...legacySystem.attributes,
        domestic_country:
            (legacySystem.attributes as { domestic_country?: CountryEnum }).domestic_country ??
            CountryEnum.INTERNATIONAL,
    };

    system.relationships.rates.data = legacySystem.relationships.rates.data.map(
        (legacyRate: LegacyRate): Rate => mapLegacyRateToCommon(legacyRate),
    );

    const legacyCurrency: LegacyCurrency | null = legacySystem.relationships.currency.data ?? null;
    system.relationships.currency.data = legacyCurrency
        ? mapLegacyCurrencyToCommon(legacyCurrency)
        : null;

    system.relationships.networks.data = legacySystem.relationships.networks.data.map(
        (legacyNetwork: LegacyNetwork): Network => mapLegacyNetworkToCommon(legacyNetwork),
    );

    return system;
}

function mapLegacyNetworkToCommon(legacyNetwork: LegacyNetwork): Network {
    const network: Network = new Network();

    network.id = legacyNetwork.id;
    network.attributes = { ...legacyNetwork.attributes };

    return network;
}

function mapLegacyRateToCommon(legacyRate: LegacyRate): Rate {
    const rate: Rate = new Rate();

    rate.id = legacyRate.id;
    rate.attributes = { ...legacyRate.attributes };

    return rate;
}

function mapLegacyCurrencyToCommon(legacyCurrency: LegacyCurrency): Currency {
    const currency: Currency = new Currency();

    currency.id = legacyCurrency.id;
    currency.attributes = { ...legacyCurrency.attributes };

    return currency;
}
